import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router';
import { UserBalanceCard, TransactionHistory } from '@/components/balance';

export const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleGoToShowcase = () => {
    navigate('/component-library');
  };

  const handleGoToAdmin = () => {
    navigate('/admin');
  };

  return (
    <div className='space-y-6'>
      <div className='text-center'>
        <h1 className='text-h1'>Welcome to your Dashboard</h1>
        <p className='text-body-2 mt-2'>Hello, {user?.name}! Here's your account information.</p>
      </div>

      <div className='grid gap-6 md:grid-cols-2 lg:grid-cols-3'>
        <UserBalanceCard onViewHistory={() => navigate('/balance/history')} />

        <Card>
          <CardHeader>
            <CardTitle>Profile Information</CardTitle>
            <CardDescription>Your personal details</CardDescription>
          </CardHeader>
          <CardContent className='space-y-2'>
            <div className='flex justify-between'>
              <span className='text-sm font-medium'>Name:</span>
              <span className='text-sm'>{user?.name}</span>
            </div>
            <div className='flex justify-between'>
              <span className='text-sm font-medium'>Email:</span>
              <span className='text-sm'>{user?.email}</span>
            </div>
            <div className='flex justify-between'>
              <span className='text-sm font-medium'>Age:</span>
              <span className='text-sm'>{user?.age}</span>
            </div>
            <div className='flex justify-between'>
              <span className='text-sm font-medium'>Member since:</span>
              <span className='text-sm'>{user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Account Status</CardTitle>
            <CardDescription>Your account information</CardDescription>
          </CardHeader>
          <CardContent className='space-y-4'>
            <div className='flex items-center justify-between'>
              <span className='text-sm font-medium'>Status:</span>
              <Badge variant='default'>Active</Badge>
            </div>
            <div className='flex items-center justify-between'>
              <span className='text-sm font-medium'>User ID:</span>
              <span className='text-sm font-mono'>{user?.id}</span>
            </div>
            <div className='flex items-center justify-between'>
              <span className='text-sm font-medium'>Last updated:</span>
              <span className='text-sm'>{user?.updatedAt ? new Date(user.updatedAt).toLocaleDateString() : 'N/A'}</span>
            </div>
            <div className='flex items-center justify-between'>
              <span className='text-sm font-medium'>Admin:</span>
              <span className='text-sm'>{user?.isAdmin ? 'Yes' : 'No'}</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
            <CardDescription>Navigate to different sections</CardDescription>
          </CardHeader>
          <CardContent className='space-y-2'>
            <Button variant='outline' className='w-full justify-start' onClick={handleGoToShowcase}>
              View Component Showcase
            </Button>
            <Button variant='outline' className='w-full justify-start' disabled>
              Edit Profile (Coming Soon)
            </Button>
            <Button variant='outline' className='w-full justify-start' disabled>
              Settings (Coming Soon)
            </Button>
            {user?.isAdmin && (
              <Button variant='outline' className='w-full justify-start' onClick={handleGoToAdmin}>
                Go to Admin Area
              </Button>
            )}
          </CardContent>
        </Card>
      </div>

      <div className='grid gap-6 lg:grid-cols-2'>
        <TransactionHistory limit={5} onViewMore={() => navigate('/balance/transactions')} />

        <Card>
          <CardHeader>
            <CardTitle>Welcome Message</CardTitle>
            <CardDescription>Thank you for using our application</CardDescription>
          </CardHeader>
          <CardContent>
            <p className='text-body-2'>
              You have successfully logged in to your account. This dashboard provides you with an overview of your
              profile information and quick access to various features. Feel free to explore the component showcase to
              see all the available UI components built with shadcn/ui.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
